import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, CheckCircle2, Download, Gift, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { LaunchOfferRefundPanel } from './LaunchOfferRefundPanel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CAMPAIGN_ID = 'launch-first-paid-10km-2026-11';
const CAMPAIGN_START_ISO = '2026-11-01T23:00:00.000Z'; // lundi 02/11/2026 à 00:00, heure de Paris
const CAMPAIGN_START_LOCAL = '2026-11-02T00:00';
const CAMPAIGN_NAME = 'Première souscription payée — Paris et petite couronne';
const DEFAULT_END_LOCAL = '2026-11-16T00:00'; // dimanche 15/11 23:59 inclus
// Novembre 2026 = heure d'hiver à Paris (UTC+1) : la saisie est toujours lue en heure de Paris
const parisLocalToDate = (value) => new Date(`${value}:00+01:00`);

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('fr-FR', { timeZone: 'Europe/Paris' });
};

export default function LaunchOfferCampaignPanel({ token }) {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [maxSlots, setMaxSlots] = useState('30');
  const [expiresAt, setExpiresAt] = useState(DEFAULT_END_LOCAL);
  const [exporting, setExporting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const exportCost = async () => {
    setExporting(true);
    try {
      const { data } = await axios.get(`${API}/admin/campaigns/launch-offer/cost.csv`, { headers, responseType: 'blob' });
      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cout-offre-lancement-${new Date().toISOString().slice(0, 10)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('L’export du coût a échoué.');
    } finally {
      setExporting(false);
    }
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/campaigns/auto`, { headers });
      const found = (data.campaigns || []).find((item) => item.campaign_id === CAMPAIGN_ID);
      setCampaign(found || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossible de lire l’état de la campagne.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const createCampaign = async (event) => {
    event.preventDefault();
    const slots = Number(maxSlots);
    if (!Number.isInteger(slots) || slots < 1 || slots > 30) {
      toast.error('La première vague est limitée à 30 abonnés maximum.');
      return;
    }
    if (!expiresAt) {
      toast.error('Choisis la date et l’heure de fin de campagne.');
      return;
    }
    const expiry = parisLocalToDate(expiresAt);
    const start = new Date(CAMPAIGN_START_ISO);
    if (Number.isNaN(expiry.getTime()) || expiry <= start) {
      toast.error('La fin de campagne doit être postérieure au lundi 2 novembre 2026 à 00:00, heure de Paris.');
      return;
    }

    const confirmed = window.confirm(
      `Créer l’offre première souscription payée : une course réelle offerte jusqu’à 10 km routiers, pour ${slots} abonnés maximum, jusqu’au ${formatDate(expiry.toISOString())} ?`
    );
    if (!confirmed) return;

    setCreating(true);
    try {
      await axios.post(`${API}/admin/campaigns/auto/create`, {
        campaign_id: CAMPAIGN_ID,
        name: CAMPAIGN_NAME,
        region: 'paris',
        max_slots: slots,
        benefit_type: 'free_first_ride',
        max_distance_km: 10,
        valid_from: CAMPAIGN_START_ISO,
        expires_at: expiry.toISOString(),
      }, { headers });
      toast.success('Campagne créée. Elle s’ouvrira à la date prévue, sans modifier les abonnements déjà vendus.');
      setMaxSlots('30');
      setExpiresAt(DEFAULT_END_LOCAL);
      await refresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'La campagne n’a pas pu être créée.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <section className="mb-6 rounded-lg border border-zinc-800 bg-[#18181B] p-5" data-testid="launch-offer-campaign-panel">
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="mb-2 flex items-center gap-2 text-[#FFD60A]">
            <Gift className="h-5 w-5" />
            <h2 className="text-lg font-bold text-white">Offre de lancement — première course offerte</h2>
          </div>
          <p className="max-w-3xl text-sm text-zinc-400">
            Réservée au premier abonnement effectivement payé, pour une adresse située à Paris (75) ou en petite couronne (92, 93, 94).
            Une seule course réelle est offerte, plafonnée à 10 km routiers. Le chauffeur est rémunéré normalement.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={exportCost} disabled={exporting} data-testid="launch-offer-export-cost-btn">
            <Download className="mr-2 h-4 w-4" />
            {exporting ? 'Export…' : 'Exporter le coût (CSV)'}
          </Button>
          <Button type="button" variant="outline" onClick={refresh} disabled={loading || creating} data-testid="launch-offer-refresh-btn">
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Actualiser
          </Button>
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-zinc-400">Lecture de la campagne…</p>
      ) : campaign ? (
        <div className="rounded-md border border-zinc-700 bg-black/20 p-4">
          <div className="mb-3 flex items-center gap-2">
            {campaign.active ? <CheckCircle2 className="h-5 w-5 text-emerald-400" /> : <AlertTriangle className="h-5 w-5 text-amber-400" />}
            <p className="font-semibold text-white">{campaign.name}</p>
          </div>
          <div className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
            <div><p className="text-zinc-500">Places attribuées</p><p className="font-semibold text-white">{campaign.slots_used} / {campaign.max_slots}</p></div>
            <div><p className="text-zinc-500">Distance maximale</p><p className="font-semibold text-white">{Math.min(Number(campaign.max_distance_km || 10), 10)} km routiers</p></div>
            <div><p className="text-zinc-500">Ouverture</p><p className="font-semibold text-white">{formatDate(campaign.valid_from)}</p></div>
            <div><p className="text-zinc-500">Fin</p><p className="font-semibold text-white">{formatDate(campaign.expires_at)}</p></div>
          </div>
          <p className="mt-3 text-xs text-zinc-500">Pour éviter les doublons, une seule campagne de ce type peut exister sous cet identifiant. Si elle existe, ses paramètres ne peuvent pas être écrasés depuis ce panneau.</p>
          <LaunchOfferRefundPanel token={token} onChanged={refresh} />
        </div>
      ) : (
        <form onSubmit={createCampaign} className="space-y-4 rounded-md border border-zinc-700 bg-black/20 p-4">
          <p className="text-sm text-zinc-300">
            La campagne n’est pas encore créée. Paramètre son plafond et sa date de fin ici. Elle ne deviendra active qu’à partir du lundi 2 novembre 2026 à 00:00, heure de Paris.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="launch-offer-max-slots">Bénéficiaires maximum (plafond pilote : 30)</Label>
              <Input
                id="launch-offer-max-slots"
                type="number"
                min="1"
                max="30"
                step="1"
                placeholder="30 maximum"
                value={maxSlots}
                onChange={(e) => setMaxSlots(e.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="launch-offer-expires-at">Fin de campagne (heure de Paris)</Label>
              <Input
                id="launch-offer-expires-at"
                type="datetime-local"
                min={CAMPAIGN_START_LOCAL}
                value={expiresAt}
                readOnly
                required
                data-testid="launch-offer-expires-at-input"
              />
              <p className="text-xs text-zinc-500">Date verrouillée : lundi 16 novembre 2026 à 00:00, heure de Paris.</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-zinc-500">Paris et petite couronne (codes postaux 75, 92, 93, 94). 30 bénéficiaires maximum. Distance routière verrouillée à 10 km. Aucun déploiement n’est déclenché par ce panneau.</p>
            <Button type="submit" disabled={creating || loading} className="bg-[#FFD60A] font-bold text-black hover:bg-[#E6C209]">
              {creating ? 'Création…' : 'Créer la campagne'}
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
